import {
  Landmark,
  Users,
  Footprints,
  Layers,
  Database,
  Network,
  Waypoints,
  type LucideIcon,
} from 'lucide-react'
import type { UiKey } from './ui'

// ============================================================================
// Site structure — the eight content areas (DESIGN-BRIEF §2 routes).
//
// This is the single source of truth for navigation, tile icons, language,
// and which WP builds each area (shown in the under-construction state). The
// front page joins this (icon + route) with front-page.ts (Finnish copy) by
// slug; the nav and footer read it directly.
// ============================================================================

export type Lang = 'fi' | 'en'

export interface Area {
  slug: string
  /** Key into ui.fi.json for the nav label. */
  labelKey: UiKey
  lang: Lang
  /** primary = the four large FI areas; secondary = the four small EN areas. */
  tier: 'primary' | 'secondary'
  icon: LucideIcon
  /** Which publication WP will build this area (for the placeholder state). */
  buildWp: string
}

// Order = nav order. Primary FI areas first; Tarkoitus sits last among them,
// just before the Synteettinen Suomi companion (Kimmo, 2026-07-10). Data-model
// is Finnish too (secondary tier = smaller nav/tiles).
// Technology and Platform choice were removed from the site (Kimmo, 2026-07-10)
// and Organization likewise (Kimmo, 2026-07-13); their page files remain on
// disk but are unreferenced (not routed, not bundled).
export const AREAS: Area[] = [
  { slug: '/kansalaiselle', labelKey: 'nav.kansalaiselle', lang: 'fi', tier: 'primary', icon: Users, buildWp: 'WP-F4' },
  { slug: '/tyontekijalle', labelKey: 'nav.tyontekijalle', lang: 'fi', tier: 'primary', icon: Footprints, buildWp: 'WP-F6' },
  { slug: '/lapileikkaus', labelKey: 'nav.lapileikkaus', lang: 'fi', tier: 'primary', icon: Layers, buildWp: 'WP-F6' },
  { slug: '/tarkoitus', labelKey: 'nav.tarkoitus', lang: 'fi', tier: 'primary', icon: Landmark, buildWp: 'WP-F5' },
  // WP-S1: the synthetic population — a featured FI companion (the data model made
  // flesh). Top-level FI nav entry; on the front page it is a featured band.
  { slug: '/synteettinen-suomi', labelKey: 'nav.synteettinenSuomi', lang: 'fi', tier: 'primary', icon: Database, buildWp: 'WP-S1' },
  { slug: '/data-model', labelKey: 'nav.dataModel', lang: 'fi', tier: 'secondary', icon: Network, buildWp: 'WP-F7' },
  // WP-V7: ArkkiValtio — the state-wide illustration-fidelity extension (09-arkkivaltio).
  { slug: '/arkkivaltio', labelKey: 'nav.arkkivaltio', lang: 'fi', tier: 'secondary', icon: Waypoints, buildWp: 'WP-V7' },
]

export const PRIMARY_AREAS = AREAS.filter((a) => a.tier === 'primary')
export const SECONDARY_AREAS = AREAS.filter((a) => a.tier === 'secondary')

export const ICON_BY_SLUG: Record<string, LucideIcon> = Object.fromEntries(
  AREAS.map((a) => [a.slug, a.icon]),
)

/** External link to the real institution (footer, DESIGN-BRIEF §3). */
export const KELA_FI_URL = 'https://www.kela.fi'

/**
 * The research model's snapshot date. The vault carries its as_of on every
 * page (objection #6 in NARRATIVE-SPINE §4); the site echoes it in the footer.
 * Sourced from the vault dashboard (META-002, updated 2026-07-07).
 */
export const MODEL_SNAPSHOT = '2026-07-07'
export const SPEC_COUNT = 563
